import { Person } from './assignee-picker.model';

/**
 * Multi-reviewer workflow-data contract (TODO #42, piloted on dubbing).
 *
 * Every review window of a workflow carries its own `reviewers[]` array hung
 * from that window's node in the task data (e.g. dubbing: the flat
 * `transcriptionReviewers` for the transcript review, and `reviewers` inside
 * each `arrLanguages[i]` for that pair's dub review). The legacy single
 * name/email fields are DUAL-WRITTEN (first ASSIGNED reviewer) while the
 * schedulers migrate to this contract.
 *
 * Invitations are NOT sent from the picker any more: an Invite-tab person is
 * just a reviewer with source "invite" and their name/email — the review task
 * sends the invitation (and the assigned/requested notifications) when it
 * activates, via the configurable app_email_template system.
 */
export type ReviewerStatus = 'ASSIGNED' | 'REQUESTED';

export interface ReviewerPayload {
  status: ReviewerStatus;
  source: 'internal' | 'vendor' | 'partner' | 'marketplace' | 'prospect' | 'invite';
  name: string;
  email: string | null;
  /** People id for internal/vendor contacts (numeric picker ids). */
  peopleId: number | null;
  /** Organization id for org-backed suppliers (partner/public orgs). */
  organizationId: number | null;
  /** directory_prospect id for ProZ prospects. */
  prospectId: number | null;
  /** How the reviewer was engaged in the picker (null = plain assign). */
  engagement: 'assign' | 'offer' | 'quote' | null;
  /** Agreed/offered rate from the engagement dialog, when any. */
  rate: number | null;
  rateUnit: string | null;
  currency: string | null;
  /** Total price when the form can compute it (e.g. wordcount × rate); else null. */
  totalPrice: number | null;
}

/** Numeric tail of a prefixed picker id ("prospect-12" → 12), or null. */
function idTail(id: string, prefix: string): number | null {
  if (!id.startsWith(prefix)) return null;
  const n = Number(id.slice(prefix.length));
  return isNaN(n) ? null : n;
}

/** Serializes one picker Person into the shared reviewer payload entry. */
export function toReviewerPayload(person: Person, totalPrice: number | null = null): ReviewerPayload {
  const prospectId = idTail(person.id, 'prospect-');
  const partnerOrgId = idTail(person.id, 'partner-');
  const publicOrgId = idTail(person.id, 'org-');
  // Plain numeric ids are directory contacts (internal/vendor people).
  const peopleId = /^\d+$/.test(person.id) && person.source !== 'marketplace'
    ? Number(person.id)
    : null;

  let source: ReviewerPayload['source'] = person.source;
  if (partnerOrgId != null) source = 'partner';
  else if (prospectId != null) source = 'prospect';

  return {
    status: person.status === 'requested' ? 'REQUESTED' : 'ASSIGNED',
    source,
    name: person.name,
    email: person.email ?? null,
    peopleId,
    organizationId: person.organizationId ?? partnerOrgId ?? publicOrgId,
    prospectId,
    engagement: person.engagement ?? null,
    rate: person.offeredRate ?? null,
    rateUnit: person.offeredUnit ?? null,
    currency: person.offeredCurrency ?? null,
    totalPrice,
  };
}

/** Legacy single-reviewer pick for the dual-written name/email fields: first ASSIGNED, else first. */
export function legacyReviewer(list: Person[]): Person | undefined {
  return list.find((p) => p.status !== 'requested') ?? list[0];
}
